import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { FileText, Camera, ShieldCheck, Globe, Landmark } from 'lucide-react';
import FacialVerification from '../../components/facial/FacialVerification';
import AadhaarVerification from '../../components/kyc/AadhaarVerification';
import PANVerification from '../../components/kyc/PANVerification';
import PassportVerification from '../../components/kyc/PassportVerification';
import { useAuth } from '../../contexts/AuthContext';
import { verificationAPI } from '../../services/api';
import BankDetailsSection from '../../components/profile/BankDetailsSection';
import KycStatus from '../../components/kyc/KycStatus';
import KYCCard from '../../components/kyc/KYCCard';
import LoaderOverlay from '../../components/common/LoaderOverlay';
import Dialog from '../../components/common/Dialog';
import { getKYCStatus } from '../../utils/kycUtils';

type StepStatus = 'pending' | 'completed';

const Kyc = () => {
  const { updateUser, user } = useAuth();
  const userData = user || JSON.parse(localStorage.getItem('user') || '{}');
  const kycStatus = getKYCStatus(user);

  const [aadhaarStatus, setAadhaarStatus] = useState<StepStatus>(
    kycStatus.isAadhaarVerified ? 'completed' : 'pending'
  );
  const [panStatus, setPanStatus] = useState<StepStatus>(
    kycStatus.isPanVerified ? 'completed' : 'pending'
  );
  const [passportStatus, setPassportStatus] = useState<StepStatus>(
    kycStatus.isPassportVerified ? 'completed' : 'pending'
  );
  const [facialStatus, setFacialStatus] = useState<StepStatus>(
    kycStatus.isFacialVerified ? 'completed' : 'pending'
  );
  const [bankStatus, setBankStatus] = useState<StepStatus>(
    kycStatus.hasBankDetails ? 'completed' : 'pending'
  );

  const [isAadhaarModalOpen, setIsAadhaarModalOpen] = useState(false);
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [documentFrontFile, setDocumentFrontFile] = useState<
    File | undefined
  >();
  const [documentFrontPreview, setDocumentFrontPreview] = useState<
    string | undefined
  >();
  const [panNumber, setPanNumber] = useState('');
  const [panDocumentFile, setPanDocumentFile] = useState<File | undefined>();
  const [panDocumentPreview, setPanDocumentPreview] = useState<
    string | undefined
  >();
  const [isPanModalOpen, setIsPanModalOpen] = useState(false);
  const [passportNumber, setPassportNumber] = useState('');
  const [passportDocumentFrontFile, setPassportDocumentFrontFile] = useState<
    File | undefined
  >();
  const [passportDocumentBackFile, setPassportDocumentBackFile] = useState<
    File | undefined
  >();
  const [passportDocumentFrontPreview, setPassportDocumentFrontPreview] =
    useState<string | undefined>();
  const [passportDocumentBackPreview, setPassportDocumentBackPreview] =
    useState<string | undefined>();
  const [isPassportModalOpen, setIsPassportModalOpen] = useState(false);
  const [isFacialModalOpen, setIsFacialModalOpen] = useState(false);
  const [isFacialLoading, setIsFacialLoading] = useState(false);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [isAadhaarLoading, setIsAadhaarLoading] = useState(false);
  const [isPanLoading, setIsPanLoading] = useState(false);
  const [isPassportLoading, setIsPassportLoading] = useState(false);
  const [isBankLoading, setIsBankLoading] = useState(false);
  const [bankFormData, setBankFormData] = useState<{
    accountName: string;
    accountNumber: string;
    bankName: string;
    branchName?: string;
    ifscCode?: string;
    bankCountry?: string;
    currency?: string;
    routingNumber?: string;
    sortCode?: string;
    bsbNumber?: string;
    iban?: string;
    bicSwift?: string;
    verificationDocumentType?: string;
    verificationDocument: File | null;
  }>({
    accountName: '',
    accountNumber: '',
    bankName: '',
    branchName: '',
    ifscCode: '',
    bankCountry: 'India',
    currency: 'INR',
    routingNumber: '',
    sortCode: '',
    bsbNumber: '',
    iban: '',
    bicSwift: '',
    verificationDocumentType: 'CANCELLED_CHEQUE',
    verificationDocument: null,
  });

  useEffect(() => {
    const currentKycStatus = getKYCStatus(user);

    setAadhaarStatus(
      currentKycStatus.isAadhaarVerified ? 'completed' : 'pending'
    );
    setPanStatus(currentKycStatus.isPanVerified ? 'completed' : 'pending');
    setPassportStatus(
      currentKycStatus.isPassportVerified ? 'completed' : 'pending'
    );
    setFacialStatus(
      currentKycStatus.isFacialVerified ? 'completed' : 'pending'
    );
    setBankStatus(currentKycStatus.hasBankDetails ? 'completed' : 'pending');
  }, [user]);

  const handleCompleteStep = (
    step: 'aadhaar' | 'pan' | 'passport' | 'facial' | 'bank'
  ) => {
    if (step === 'aadhaar') {
      setIsAadhaarModalOpen(true);
      return;
    }
    if (step === 'pan') {
      setIsPanModalOpen(true);
      return;
    }

    if (step === 'passport') {
      setIsPassportModalOpen(true);
      return;
    }
    if (step === 'bank') {
      setIsBankModalOpen(true);
      return;
    }
    setIsFacialModalOpen(true);
  };

  const handleAadhaarVerificationSuccess = () => {
    setAadhaarStatus('completed');
    updateUser({
      verification: {
        isAadhaarVerified: true,
      },
    });
    setIsAadhaarModalOpen(false);
  };

  const handleAadharNumberChange = (value: string) => {
    setAadhaarNumber(value);
  };

  const handleDocumentFrontUpload = (file: File) => {
    setDocumentFrontFile(file);
    const objectUrl = URL.createObjectURL(file);
    setDocumentFrontPreview(objectUrl);
  };

  const handleRemoveDocumentFront = () => {
    setDocumentFrontFile(undefined);
    setDocumentFrontPreview(undefined);
  };

  const handlePanVerificationSuccess = () => {
    setPanStatus('completed');
    updateUser({
      verification: {
        isPanVerified: true,
      },
    });
    setIsPanModalOpen(false);
    toast.success('PAN verification completed');
  };

  const handlePanNumberChange = (value: string) => {
    setPanNumber(value);
  };

  const handlePanDocumentUpload = (file: File) => {
    setPanDocumentFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPanDocumentPreview(objectUrl);
  };

  const handleRemovePanDocument = () => {
    setPanDocumentFile(undefined);
    setPanDocumentPreview(undefined);
  };

  const handlePassportVerificationSuccess = () => {
    setPassportStatus('completed');
    updateUser({
      verification: {
        isPassportVerified: true,
      },
    });
    setIsPassportModalOpen(false);
  };

  const handleBankVerificationSuccess = () => {
    setBankStatus('completed');
    updateUser({
      bankDetails: {
        accountName: bankFormData.accountName,
        accountNumber: bankFormData.accountNumber,
        bankName: bankFormData.bankName,
        bankCountry: bankFormData.bankCountry,
        currency: bankFormData.currency,
        branchName: bankFormData.branchName,
        ifscCode: bankFormData.ifscCode
          ? bankFormData.ifscCode.toUpperCase()
          : undefined,
        routingNumber: bankFormData.routingNumber,
        sortCode: bankFormData.sortCode,
        bsbNumber: bankFormData.bsbNumber,
        iban: bankFormData.iban ? bankFormData.iban.toUpperCase() : undefined,
        bicSwift: bankFormData.bicSwift
          ? bankFormData.bicSwift.toUpperCase()
          : undefined,
        verificationDocumentType: bankFormData.verificationDocumentType,
      },
    });
    setIsBankModalOpen(false);
  };

  const handlePassportNumberChange = (value: string) => {
    setPassportNumber(value);
  };

  const handlePassportDocumentFrontUpload = (file: File) => {
    setPassportDocumentFrontFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPassportDocumentFrontPreview(objectUrl);
  };

  const handlePassportDocumentBackUpload = (file: File) => {
    setPassportDocumentBackFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPassportDocumentBackPreview(objectUrl);
  };

  const handleRemovePassportDocumentFront = () => {
    setPassportDocumentFrontFile(undefined);
    setPassportDocumentFrontPreview(undefined);
  };

  const handleRemovePassportDocumentBack = () => {
    setPassportDocumentBackFile(undefined);
    setPassportDocumentBackPreview(undefined);
  };

  const handleAadhaarSubmit = async () => {
    const fullName = userData?.fullName || '';
    const dateOfBirth = userData?.dob || '';

    if (
      !fullName ||
      !dateOfBirth ||
      aadhaarNumber.length !== 12 ||
      !documentFrontFile
    ) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsAadhaarLoading(true);
    try {
      const formattedDob = moment(dateOfBirth).format('DD/MM/YYYY');
      const response = await verificationAPI.verifyAadhaar({
        name: fullName,
        dob: formattedDob,
        aadhaarNumber: aadhaarNumber,
        document: documentFrontFile,
      });

      if (response.success) {
        const aadhaarVerifiedDate = moment().format('YYYY-MM-DD');
        const storeResponse =
          await verificationAPI.storeAadhaarVerificationData({
            aadharNumber: aadhaarNumber,
            isAadhaarVerified: true,
            aadharVerifiedDate: aadhaarVerifiedDate,
          });
        toast.success(storeResponse.message);
        handleAadhaarVerificationSuccess();
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(errorMessage);
    } finally {
      setIsAadhaarLoading(false);
    }
  };

  const handlePanSubmit = async () => {
    const fullName = userData?.fullName || '';
    const dateOfBirth = userData?.dob || '';

    if (
      !fullName ||
      !dateOfBirth ||
      panNumber.length !== 10 ||
      !panDocumentFile
    ) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsPanLoading(true);
    try {
      const formattedDob = moment(dateOfBirth).format('DD/MM/YYYY');
      const response = await verificationAPI.verifyPan({
        name: fullName,
        dob: formattedDob,
        panNumber: panNumber,
        document: panDocumentFile,
      });

      if (response.success) {
        const panVerifiedDate = moment().format('YYYY-MM-DD');
        await verificationAPI.storePanVerificationData({
          panNumber: panNumber,
          isPanVerified: true,
          panVerifiedDate: panVerifiedDate,
        });

        handlePanVerificationSuccess();
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      console.error('PAN verification error:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(errorMessage);
    } finally {
      setIsPanLoading(false);
    }
  };

  const handlePassportSubmit = async () => {
    const fullName = userData?.fullName || '';
    const dateOfBirth = userData?.dob || '';

    if (
      !fullName ||
      !dateOfBirth ||
      passportNumber.length < 6 ||
      !passportDocumentFrontFile ||
      !passportDocumentBackFile
    ) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsPassportLoading(true);
    try {
      const formattedDob = moment(dateOfBirth).format('DD/MM/YYYY');
      const response = await verificationAPI.verifyPassport({
        name: fullName,
        dob: formattedDob,
        passportNumber: passportNumber,
        documentFront: passportDocumentFrontFile,
        documentBack: passportDocumentBackFile,
      });

      if (response.success) {
        const passportVerifiedDate = moment().format('YYYY-MM-DD');
        const storeResponse =
          await verificationAPI.storePassportVerificationData({
            passportNumber: passportNumber,
            isPassportVerified: true,
            passportVerifiedDate: passportVerifiedDate,
          });

        toast.success(storeResponse.message);
        handlePassportVerificationSuccess();
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      console.error('Passport verification error:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(errorMessage);
    } finally {
      setIsPassportLoading(false);
    }
  };

  const handleFacialCapture = async (file: File, imageData?: string) => {
    setIsFacialLoading(true);
    try {
      const response = await verificationAPI.storeFacialVerificationData({
        facialVerification: file,
      });

      if (response.success) {
        updateUser({
          verification: {
            facialVerification:
              response.data?.facialVerification || imageData || 'captured',
            isAadhaarVerified: aadhaarStatus === 'completed',
            isPanVerified: panStatus === 'completed',
            isPassportVerified: passportStatus === 'completed',
            emailVerified: true,
            phoneVerified: true,
            aadharNumber: null,
            panNumber: null,
            addressDocument: null,
            passportNumber: null,
          },
        });

        setFacialStatus('completed');
        toast.success(response.message);
        setIsFacialModalOpen(false);
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(errorMessage);
    } finally {
      setIsFacialLoading(false);
    }
  };

  const isKycComplete =
    (aadhaarStatus === 'completed' ||
      panStatus === 'completed' ||
      passportStatus === 'completed') &&
    facialStatus === 'completed' &&
    bankStatus === 'completed';

  return (
    <div className="space-y-6">
      <div className="pt-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>KYC Verification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col gap-6">
              <div className="max-w-3xl">
                <h3 className="text-lg font-semibold text-gray-900">
                  Complete your KYC to unlock full trading access
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Provide your documents and complete verification so you can
                  deposit, withdraw, and trade without limits.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 justify-center">
                <KYCCard
                  icon={FileText}
                  title="Id"
                  description="Submit your Aadhaar card and complete verification."
                  status={aadhaarStatus}
                  onVerify={() => handleCompleteStep('aadhaar')}
                  bgColor="bg-blue-50"
                  iconColor="text-blue-600"
                />
                <KYCCard
                  icon={ShieldCheck}
                  title="PAN"
                  description="Upload your PAN and verify your identity in seconds."
                  status={panStatus}
                  onVerify={() => handleCompleteStep('pan')}
                  bgColor="bg-green-50"
                  iconColor="text-yellow-600"
                />
                <KYCCard
                  icon={Globe}
                  title="Passport"
                  description="Upload your passport for international verification."
                  status={passportStatus}
                  onVerify={() => handleCompleteStep('passport')}
                  bgColor="bg-orange-50"
                  iconColor="text-orange-600"
                />
                <KYCCard
                  icon={Camera}
                  title="Facial"
                  description="Complete a quick selfie scan to prove it's you."
                  status={facialStatus}
                  onVerify={() => handleCompleteStep('facial')}
                  bgColor="bg-purple-50"
                  iconColor="text-purple-600"
                />
                <KYCCard
                  icon={Landmark}
                  title="Bank"
                  description="Complete your bank details"
                  status={bankStatus}
                  onVerify={() => handleCompleteStep('bank')}
                  bgColor="bg-purple-50"
                  iconColor="text-purple-600"
                />
              </div>
            </div>
          </CardContent>
        </Card>
        <KycStatus
          data={{
            aadhaarStatus,
            panStatus,
            passportStatus,
            facialStatus,
            bankStatus,
            isKycComplete,
          }}
        />
      </div>

      <Dialog
        open={isAadhaarModalOpen}
        onOpenChange={setIsAadhaarModalOpen}
        title="Id Verification"
        onSubmit={handleAadhaarSubmit}
        submitText="Verify Aadhaar"
        disabled={!aadhaarNumber || !documentFrontFile}
        onCancel={() => setIsAadhaarModalOpen(false)}
        isLoading={isAadhaarLoading}
        closeOnOutsideClick={false}
      >
        <LoaderOverlay
          isLoading={isAadhaarLoading}
          loadingText="Verifying Aadhaar..."
        >
          <AadhaarVerification
            aadharNumber={aadhaarNumber}
            onAadharNumberChange={handleAadharNumberChange}
            onDocumentFrontUpload={handleDocumentFrontUpload}
            documentFrontPreview={documentFrontPreview}
            documentFrontFile={documentFrontFile}
            onRemoveDocumentFront={handleRemoveDocumentFront}
          />
        </LoaderOverlay>
      </Dialog>

      <Dialog
        open={isPanModalOpen}
        onOpenChange={setIsPanModalOpen}
        title="PAN Verification"
        onSubmit={handlePanSubmit}
        submitText="Verify PAN"
        disabled={!panNumber || !panDocumentFile}
        onCancel={() => setIsPanModalOpen(false)}
        isLoading={isPanLoading}
        closeOnOutsideClick={false}
      >
        <LoaderOverlay isLoading={isPanLoading} loadingText="Verifying PAN...">
          <PANVerification
            panNumber={panNumber}
            onPanNumberChange={handlePanNumberChange}
            onDocumentFrontUpload={handlePanDocumentUpload}
            documentFrontPreview={panDocumentPreview}
            documentFrontFile={panDocumentFile}
            onRemoveDocumentFront={handleRemovePanDocument}
          />
        </LoaderOverlay>
      </Dialog>

      <Dialog
        open={isPassportModalOpen}
        onOpenChange={setIsPassportModalOpen}
        title="Passport Verification"
        onSubmit={handlePassportSubmit}
        submitText="Verify Passport"
        disabled={
          !passportNumber ||
          !passportDocumentFrontFile ||
          !passportDocumentBackFile
        }
        onCancel={() => setIsPassportModalOpen(false)}
        isLoading={isPassportLoading}
        closeOnOutsideClick={false}
      >
        <LoaderOverlay
          isLoading={isPassportLoading}
          loadingText="Verifying Passport..."
        >
          <PassportVerification
            passportNumber={passportNumber}
            onPassportNumberChange={handlePassportNumberChange}
            onDocumentFrontUpload={handlePassportDocumentFrontUpload}
            onDocumentBackUpload={handlePassportDocumentBackUpload}
            documentFrontPreview={passportDocumentFrontPreview}
            documentBackPreview={passportDocumentBackPreview}
            documentFrontFile={passportDocumentFrontFile}
            documentBackFile={passportDocumentBackFile}
            onRemoveDocumentFront={handleRemovePassportDocumentFront}
            onRemoveDocumentBack={handleRemovePassportDocumentBack}
          />
        </LoaderOverlay>
      </Dialog>

      <Dialog
        open={isBankModalOpen}
        onOpenChange={setIsBankModalOpen}
        title="Bank Details Verification"
        maxWidth="2xl"
        onCancel={() => setIsBankModalOpen(false)}
        closeOnOutsideClick={false}
        footer={null}
      >
        <BankDetailsSection
          user={null}
          pageLoading={isBankLoading}
          setPageLoading={setIsBankLoading}
          isAddMode={true}
          isOpen={isBankModalOpen}
          onClose={() => setIsBankModalOpen(false)}
          onSuccess={handleBankVerificationSuccess}
          onFormDataChange={setBankFormData}
          hideSubmitButtons={false}
        />
      </Dialog>

      <FacialVerification
        isOpen={isFacialModalOpen}
        onClose={() => setIsFacialModalOpen(false)}
        onCapture={handleFacialCapture}
        title="Facial Verification"
        isLoading={isFacialLoading}
      />
    </div>
  );
};

export default Kyc;
